'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Clock, ChevronRight } from 'lucide-react';
import { cn, formatNaira, formatNumber, calculateProgress, vaultSpring } from '../lib/utils';
import { useCountdown, useViewport } from '../hooks';
import { Card } from './Card';
import { Button, ShimmerButton } from './Button';

export interface PropertyData {
  id: string;
  title: string;
  description: string;
  location: string;
  marketValue: number;
  ticketPrice: number;
  totalTickets: number;
  ticketsSold: number;
  drawDate: string;
  status: 'active' | 'closed' | 'drawn';
  images: string[];
  features?: string[];
}

export interface PropertyCardProps {
  property: PropertyData;
  onClick?: () => void;
  className?: string;
}

/**
 * Property listing card with progress bar and countdown
 */
export const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onClick,
  className,
}) => {
  const countdown = useCountdown(property.drawDate);
  const progress = calculateProgress(property.ticketsSold, property.totalTickets);

  const statusBadge = {
    active: { class: 'status-active', label: 'Active' },
    closed: { class: 'status-closed', label: 'Closed' },
    drawn: { class: 'status-drawn', label: 'Drawn' },
  };

  return (
    <Card
      variant="interactive"
      hover
      glow={property.status === 'active'}
      className={cn('overflow-hidden', className)}
      onClick={onClick}
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={property.images[0]}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={cn('status-badge', statusBadge[property.status].class)}>
            {statusBadge[property.status].label}
          </span>
        </div>

        {/* Price Overlay */}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-surface-0/90 to-transparent p-4">
          <p className="text-xs text-text-muted uppercase tracking-wider">Market Value</p>
          <p className="font-mono text-xl font-bold text-text-primary tabular-nums">
            {formatNaira(property.marketValue)}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Title & Location */}
        <h3 className="font-display text-lg font-semibold text-text-primary line-clamp-2 mb-2">
          {property.title}
        </h3>
        <p className="flex items-center gap-1.5 text-sm text-text-secondary mb-4">
          <MapPin className="w-4 h-4 text-gold-500" />
          {property.location}
        </p>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-text-muted">Tickets Sold</span>
            <span className="font-mono text-text-primary tabular-nums">
              {formatNumber(property.ticketsSold)} / {formatNumber(property.totalTickets)}
            </span>
          </div>
          <div className="progress-bar">
            <motion.div
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Countdown (Active only) */}
        {property.status === 'active' && !countdown.isExpired && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-surface-2/50">
            <Clock className="w-4 h-4 text-gold-500" />
            <span className="text-xs text-text-muted">Draw in:</span>
            <span className="font-mono text-sm font-semibold text-text-primary tabular-nums">
              {countdown.days}d {countdown.hours}h {countdown.minutes}m
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div>
            <p className="text-xs text-text-muted">Per Ticket</p>
            <p className="font-mono text-lg font-bold text-gold-500 tabular-nums">
              {formatNaira(property.ticketPrice)}
            </p>
          </div>
          <motion.div
            whileHover={{ x: 4 }}
            transition={vaultSpring}
          >
            <ChevronRight className="w-6 h-6 text-gold-500" />
          </motion.div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Featured property hero card with large presentation
 */
export interface FeaturedPropertyProps {
  property: PropertyData;
  onViewDetails?: () => void;
  onBuyTicket?: () => void;
  className?: string;
}

export const FeaturedProperty: React.FC<FeaturedPropertyProps> = ({
  property,
  onViewDetails,
  onBuyTicket,
  className,
}) => {
  const { isDesktop } = useViewport();
  const countdown = useCountdown(property.drawDate);
  const progress = calculateProgress(property.ticketsSold, property.totalTickets);

  return (
    <div className={cn('relative', className)}>
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden rounded-vault-xl">
        <img
          src={property.images[0]}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-surface-0 via-surface-0/80 to-transparent" />
      </div>

      {/* Content */}
      <div className={cn(
        'relative z-10',
        isDesktop ? 'grid grid-cols-2 gap-12 p-12' : 'p-6'
      )}>
        {/* Left: Property Info */}
        <div className="space-y-6">
          {/* Badge */}
          <span className="status-badge status-active">
            Featured Property
          </span>

          {/* Title */}
          <div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-3">
              {property.title}
            </h2>
            <p className="flex items-center gap-2 text-text-secondary">
              <MapPin className="w-5 h-5 text-gold-500" />
              {property.location}
            </p>
          </div>

          {/* Description */}
          <p className="text-text-secondary line-clamp-3 max-w-lg">
            {property.description}
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-vault bg-surface-1/60 backdrop-blur-sm border border-white/10">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Market Value</p>
              <p className="font-mono text-xl font-bold text-text-primary tabular-nums">
                {formatNaira(property.marketValue)}
              </p>
            </div>
            <div className="p-4 rounded-vault bg-surface-1/60 backdrop-blur-sm border border-white/10">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Ticket Price</p>
              <p className="font-mono text-xl font-bold text-gold-500 tabular-nums">
                {formatNaira(property.ticketPrice)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <ShimmerButton size="lg" onClick={onBuyTicket}>
              Buy Tickets
            </ShimmerButton>
            <Button variant="outline" size="lg" onClick={onViewDetails}>
              View Details
            </Button>
          </div>
        </div>

        {/* Right: Progress & Countdown (Desktop) */}
        {isDesktop && (
          <div className="flex items-center justify-center">
            <div className="w-full max-w-sm space-y-6 p-6 rounded-vault-xl bg-surface-1/80 backdrop-blur-sm border border-white/10">
              {/* Countdown */}
              {!countdown.isExpired && (
                <div className="text-center">
                  <p className="text-sm text-text-muted mb-3">Draw Countdown</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: countdown.days, label: 'Days' },
                      { value: countdown.hours, label: 'Hours' },
                      { value: countdown.minutes, label: 'Min' },
                      { value: countdown.seconds, label: 'Sec' },
                    ].map((item) => (
                      <div key={item.label} className="p-3 rounded-lg bg-surface-2 border border-white/5">
                        <p className="font-mono text-2xl font-bold text-text-primary tabular-nums">
                          {item.value.toString().padStart(2, '0')}
                        </p>
                        <p className="text-xs text-text-muted">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-text-muted">Tickets Sold</span>
                  <span className="font-mono text-text-primary tabular-nums">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="progress-bar h-3">
                  <motion.div
                    className="progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-center text-sm text-text-muted mt-2">
                  {formatNumber(property.ticketsSold)} of {formatNumber(property.totalTickets)} sold
                </p>
              </div>

              {/* Participants */}
              <div className="flex items-center justify-center gap-2 text-sm text-text-secondary">
                <Users className="w-4 h-4 text-gold-500" />
                <span>{formatNumber(property.ticketsSold)} participants</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Property grid with responsive layout
 */
export interface PropertyGridProps {
  properties: PropertyData[];
  onPropertyClick?: (property: PropertyData) => void;
  className?: string;
}

export const PropertyGrid: React.FC<PropertyGridProps> = ({
  properties,
  onPropertyClick,
  className,
}) => {
  return (
    <div className={cn(
      'grid gap-6',
      'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      className
    )}>
      {properties.map((property) => (
        <PropertyCard
          key={property.id}
          property={property}
          onClick={() => onPropertyClick?.(property)}
        />
      ))}
    </div>
  );
};
